// Match card agreement — the read model (Lisa, 2026-07-23).
//
// The card's date and time are agreed by three parties on an organiser-created
// match (admin + both captains) or two on a captain-proposed one (the two
// captains — the organiser is not a signatory on a fixture they did not
// schedule). Completing the set is what BUILDS the card.
//
// WHY THIS EXISTS AS A SEPARATE READ MODEL: every one of these facts was
// already in the database and on the Discord DMs, and NONE of it was on the
// match card page. An admin looking at a card that had not built could not
// tell whether it was waiting on a captain or blocked by an objection — the
// same "real state, invisible on screen" failure as the verification strip's
// vacuous green (see matchVerifyChecklist.ts's F9 comment).
//
// PURE, so the state logic is testable without mounting a 2900-line page —
// same discipline as deriveMatchVerifyChecklist next door. The server remains
// the authority on who may sign; this only decides what to DRAW.

import type { AgreementParty, AgreementLine, MatchCardV2Response } from './api';

const ALL_PARTIES: AgreementParty[] = ['admin', 'clan1', 'clan2'];

type MatchRow = MatchCardV2Response['match'] & Record<string, unknown>;

/**
 * The party set in force for this match, as stored at creation.
 *
 * Falls back to ALL THREE when absent — matching the server's
 * requiredParties() exactly, including its reasoning: a legacy row predates
 * the feature, and the strict default can only ever ask for one more
 * signature, never let a card through on one nobody gave. The two must agree
 * or the UI would show a card as complete that the server refuses to build.
 */
export function requiredParties(match: MatchRow): AgreementParty[] {
  const stored = match.cardAgreementParties;
  if (Array.isArray(stored) && stored.length > 0) {
    return stored.filter((p): p is AgreementParty => ALL_PARTIES.includes(p as AgreementParty));
  }
  return [...ALL_PARTIES];
}

export interface AgreementView {
  lines: AgreementLine[];
  /** Every party in force has agreed and nobody objects. */
  complete: boolean;
  /** Someone has actively refused this time — the card cannot build. */
  blocked: boolean;
  agreedCount: number;
  totalCount: number;
}

/**
 * Turns the raw match row into the lines to draw.
 *
 * `objected` deliberately outranks `agreed` when reading a party's state: a
 * disagree WITHDRAWS that party's agreement server-side, so the two stamps can
 * never both be set — but if a legacy or half-written row ever carried both,
 * showing the refusal is the safe way to be wrong.
 */
export function deriveAgreementView(
  match: MatchRow,
  labels: { clan1: string; clan2: string },
): AgreementView {
  const parties = requiredParties(match);

  const lines: AgreementLine[] = parties.map(party => {
    const agreedAt = match[`${party}CardAgreedAt`];
    const disagreedAt = match[`${party}CardDisagreedAt`];
    const state: AgreementLine['state'] =
      typeof disagreedAt === 'number' ? 'objected'
      : typeof agreedAt === 'number' ? 'agreed'
      : 'waiting';
    const by = state === 'objected'
      ? match[`${party}CardDisagreedBy`]
      : match[`${party}CardAgreedBy`];
    return {
      party,
      label: party === 'admin' ? 'Competition admin' : party === 'clan1' ? labels.clan1 : labels.clan2,
      state,
      ...(typeof by === 'string' && by ? { by } : {}),
      ...(state === 'agreed' && match[`${party}CardAgreedAuto`] === true ? { auto: true } : {}),
    };
  });

  const blocked = lines.some(l => l.state === 'objected');
  const agreedCount = lines.filter(l => l.state === 'agreed').length;

  return {
    lines,
    blocked,
    complete: !blocked && agreedCount === lines.length,
    agreedCount,
    totalCount: lines.length,
  };
}

/**
 * Which line this viewer may sign, or null.
 *
 * UX gating only — the server derives the party independently from the match
 * row and refuses anything else (NOT_YOUR_SIDE / NOT_AN_ORGANISER /
 * NOT_A_PARTY). Getting this wrong shows a button that then 403s; it cannot
 * produce a signature nobody was entitled to give.
 *
 * An organiser sees the ADMIN line as theirs, but only when the admin party is
 * actually in force — on a captain-proposed match there is no organiser line
 * to sign. They can still act for a stuck side via `party` on the API, which
 * is a deliberate operator power, not this button.
 */
export function viewerParty(
  match: MatchRow,
  viewer: { discordId?: string; runsSide1: boolean; runsSide2: boolean; isOrganiser: boolean },
): AgreementParty | null {
  const parties = requiredParties(match);
  if (viewer.runsSide1 && parties.includes('clan1')) return 'clan1';
  if (viewer.runsSide2 && parties.includes('clan2')) return 'clan2';
  if (viewer.isOrganiser && parties.includes('admin')) return 'admin';
  return null;
}

/** One-line summary for a collapsed/at-a-glance rendering. */
export function agreementSummary(view: AgreementView): string {
  if (view.blocked) {
    const who = view.lines.filter(l => l.state === 'objected').map(l => l.label).join(', ');
    return `${who} cannot make this time`;
  }
  if (view.complete) return 'Everyone has agreed';
  const waiting = view.lines.filter(l => l.state === 'waiting').map(l => l.label);
  return `Waiting on ${waiting.join(' and ')}`;
}
